/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Design Automation team for Inventor
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from "react";
import { connect } from "react-redux";
import "react-base-table/styles.css";
import "./bom.css";

import { getActiveProject } from "../reducers/mainReducer";
import repo from "../Repository";
import Button from "@hig/button";

const latinize = (s) =>
    (s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/ł/g, "l")
        .replace(/ą/g, "a")
        .replace(/ć/g, "c")
        .replace(/ę/g, "e")
        .replace(/ń/g, "n")
        .replace(/ó/g, "o")
        .replace(/ś/g, "s")
        .replace(/ż/g, "z")
        .replace(/ź/g, "z");

class Bom extends Component {
    state = {
        loading: true,
        source: "",
        rows: [],
        hintShown: false,
        tables: 0,
        benches: 0,
        chairs: 0
    };

    componentDidMount() {
        this.loadServerBom();
        window.addEventListener("bom-from-viewer-ready", this.onViewerBom);
        window.addEventListener("assembly-counts-ready", this.onCounts);

        if (window._asmCounts) {
            const { tables, benches, chairs } = window._asmCounts;
            this.setState({
                tables: (tables || []).length,
                benches: (benches || []).length,
                chairs: (chairs || []).length
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener("bom-from-viewer-ready", this.onViewerBom);
        window.removeEventListener("assembly-counts-ready", this.onCounts);
    }

    onCounts = (e) => {
        const data = e?.detail || window._asmCounts || {};
        this.setState({
            tables: (data.tables || []).length || 0,
            benches: (data.benches || []).length || 0,
            chairs: (data.chairs || []).length || 0
        });
    };

    onViewerBom = (e) => {
        const rows = (e?.detail?.rows || window._bomFromViewer || []).slice();
        if (rows.length) {
            this.setState({ rows, loading: false, source: "viewer" });
        }
    };

    async loadServerBom() {
        const url = this.props.activeProject?.bomJsonUrl;
        if (!url) {
            const rows = (window._bomFromViewer || []).slice();
            if (rows.length) {
                this.setState({ rows, loading: false, source: "viewer" });
            } else {
                this.setState({ loading: false, source: "", rows: [] });
            }
            return;
        }

        try {
            const data = await repo.loadBom(url);
            const serverRows = Array.isArray(data?.data)
                ? data.data.map((arr) => ({
                    row: arr[0],
                    pn: arr[1],
                    qty: arr[2],
                    desc: arr[3],
                    mat: arr[4]
                }))
                : [];

            if (serverRows.length) {
                this.setState({ rows: serverRows, loading: false, source: "server" });
                return;
            }

            const viewerRows = (window._bomFromViewer || []).slice();
            if (viewerRows.length) {
                this.setState({ rows: viewerRows, loading: false, source: "viewer" });
            } else {
                this.setState({ loading: false, source: "", rows: [], hintShown: true });
            }
        } catch (e) {
            const viewerRows = (window._bomFromViewer || []).slice();
            if (viewerRows.length) {
                this.setState({ rows: viewerRows, loading: false, source: "viewer" });
            } else {
                this.setState({ loading: false, source: "", rows: [], hintShown: true });
            }
        }
    }

    exportCsv = () => {
        const { rows } = this.state;

        if (!rows || rows.length === 0) {
            alert("Brak danych BOM do eksportu.");
            return;
        }

        const header = ["Pozycja", "Nazwa części", "Ilość", "Opis", "Materiał"];

        const escapeCsv = (value) =>
            '"' + (value == null ? "" : String(value).replace(/"/g, '""')) + '"';

        const lines = [header.join(";")];

        rows.forEach((row, index) => {
            const line = [
                index + 1,
                row.pn || "",
                row.qty || "",
                row.desc || "",
                row.mat || ""
            ].map(escapeCsv);

            lines.push(line.join(";"));
        });

        const csvContent = lines.join("\r\n");
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvContent], {
            type: "text/csv;charset=utf-8;"
        });

        const url = URL.createObjectURL(blob);

        const projectName =
            (this.props.activeProject &&
                (this.props.activeProject.label || this.props.activeProject.name)) ||
            "bom";

        const a = document.createElement("a");
        a.href = url;
        a.download = `${projectName}_bom.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    renderHeaderNote() {
        const { hintShown, rows, loading } = this.state;
        if (loading) return <div style={{ padding: 12 }}>Loading BOM…</div>;
        if (!rows.length && hintShown) {
            return (
                <div style={{ padding: 12 }}>
                    BOM z serwera jest pusty. Otwórz zakładkę <strong>Model</strong>, poczekaj
                    aż model się załaduje, wróć tutaj – BOM zbuduje się automatycznie.
                </div>
            );
        }
        return null;
    }

    renderFlatTable(rows) {
        return (
            <div style={{ padding: 12 }}>
                <table className="bomTable">
                    <thead>
                        <tr>
                            <th>Pozycja</th>
                            <th>Nazwa części</th>
                            <th>Ilość</th>
                            <th>Opis</th>
                            <th>Materiał</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={`${latinize(r.pn)}-${i}`}>
                                <td style={{ width: 120, textAlign: "left", paddingLeft: 8 }}>
                                    {i + 1}
                                </td>
                                <td style={{ width: 360 }}>{r.pn}</td>
                                <td style={{ width: 140, textAlign: "left", paddingLeft: 8 }}>
                                    {r.qty}
                                </td>
                                <td style={{ width: 420 }}>{r.desc || ""}</td>
                                <td style={{ width: 260 }}>{r.mat || ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderTable() {
        const { rows } = this.state;
        if (!rows.length) return <div style={{ padding: 16 }}>Brak listy części.</div>;

        const hasTopLevel =
            (this.state.tables || 0) > 0 ||
            (this.state.benches || 0) > 0 ||
            (this.state.chairs || 0) > 0;

        const isStolPN = (pn) => latinize(pn).startsWith("stol");
        const isLawkaPN = (pn) => latinize(pn).startsWith("lawka");

        const hasStolPN = rows.some((r) => isStolPN(r.pn));
        const hasLawkaPN = rows.some((r) => isLawkaPN(r.pn));

        const isFurniture = hasTopLevel || hasStolPN || hasLawkaPN;

        if (!isFurniture) {
            return this.renderFlatTable(rows);
        }

        const stolRows = rows.filter((r) => isStolPN(r.pn));
        const lawkaRows = rows.filter((r) => isLawkaPN(r.pn));
        const krzesloRows = rows.filter(
            (r) => !isStolPN(r.pn) && !isLawkaPN(r.pn)
        );

        const tablesCount =
            (this.state.tables || 0) > 0
                ? this.state.tables
                : stolRows.reduce((s, r) => s + (Number(r.qty) || 0), 0);

        const benchesCount =
            (this.state.benches || 0) > 0
                ? this.state.benches
                : lawkaRows.reduce((s, r) => s + (Number(r.qty) || 0), 0);

        const chairsCount = this.state.chairs || 0;

        const renderSectionHeader = (label) => (
            <tr className="bom-section-header">
                <td colSpan={5}>{label}</td>
            </tr>
        );

        const renderRow = (i, r) => (
            <tr key={`${latinize(r.pn)}-${i}`}>
                <td style={{ width: 120, textAlign: "left", paddingLeft: 8 }}>{i}</td>
                <td style={{ width: 360 }}>{r.pn}</td>
                <td style={{ width: 140, textAlign: "left", paddingLeft: 8 }}>
                    {r.qty}
                </td>
                <td style={{ width: 420 }}>{r.desc || ""}</td>
                <td style={{ width: 260 }}>{r.mat || ""}</td>
            </tr>
        );

        const mapWithIndex = (arr) => arr.map((r, idx) => renderRow(idx + 1, r));

        return (
            <div style={{ padding: 12 }}>
                <table className="bomTable">
                    <thead>
                        <tr>
                            <th>Pozycja</th>
                            <th>Nazwa części</th>
                            <th>Ilość</th>
                            <th>Opis</th>
                            <th>Materiał</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(tablesCount > 0 || stolRows.length > 0) && (
                            <>
                                {renderSectionHeader(`Stół ×${tablesCount || stolRows.length || 0}`)}
                                {mapWithIndex(stolRows)}
                            </>
                        )}

                        {(chairsCount > 0 || krzesloRows.length > 0) && (
                            <>
                                {renderSectionHeader(`Krzesło ×${chairsCount}`)}
                                {mapWithIndex(krzesloRows)}
                            </>
                        )}

                        {(benchesCount > 0 || lawkaRows.length > 0) && (
                            <>
                                {renderSectionHeader(`Ławka ×${benchesCount || lawkaRows.length || 0}`)}
                                {mapWithIndex(lawkaRows)}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { rows, loading } = this.state;

        return (
            <div
                className="fullheight"
                style={{ display: "flex", flexDirection: "column" }}
            >
                <div
                    style={{
                        padding: "8px 12px",
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        borderBottom: "1px solid #ddd"
                    }}
                >
                    <Button
                        className="orangeButton"
                        title="Eksport BOM"
                        onClick={this.exportCsv}
                        size="standard"
                        disabled={loading || !rows || rows.length === 0}
                    >
                        Eksport BOM
                    </Button>
                </div>

                {this.renderHeaderNote()}

                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {this.renderTable()}
                </div>
            </div>
        );
    }
}

export default connect((store) => ({
    activeProject: getActiveProject(store)
}))(Bom);